import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import LinkIcon from "@mui/icons-material/Link";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import { useQuery } from "react-query";
import { useUsers } from "../../hooks/useUsers";
import { usePositions } from "../../hooks/usePositions";
import { useCompanies } from "../../hooks/useCompanies";
import {
  fetchGoogleCalendarList,
  getGoogleCalendarOauthUrl,
  getGoogleCalendarStatus,
} from "../../api/client";
import { AuthContext } from "../../context/AuthContext";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";

const emptyForm = {
  name: "",
  type: "shared",
  color: "#7b1fa2",
  permission: "manage",
  shareTargets: ["users"],
  users: [],
  positions: [],
  companies: [],
  googleCalendarId: "",
};

const colorOptions = ["#7b1fa2", "#1976d2", "#00897b", "#ef6c00", "#d81b60", "#5d4037"];

const calendarTypeOptions = [
  {
    value: "shared",
    title: "Agenda de equipe",
    description: "Para compromissos que um grupo precisa acompanhar ou movimentar junto.",
    Icon: GroupOutlinedIcon,
  },
  {
    value: "resource",
    title: "Sala ou recurso",
    description: "Para reservar sala, veículo, equipamento ou outro recurso compartilhado.",
    Icon: MeetingRoomOutlinedIcon,
  },
];

const shareTargetOptions = [
  {
    value: "users",
    title: "Pessoas",
    description: "Escolha usuários específicos.",
    Icon: GroupOutlinedIcon,
  },
  {
    value: "positions",
    title: "Cargos",
    description: "Dê acesso para uma função inteira.",
    Icon: BadgeOutlinedIcon,
  },
  {
    value: "companies",
    title: "Empresas/projetos",
    description: "Compartilhe por empresa ou projeto.",
    Icon: BusinessOutlinedIcon,
  },
];

function optionLabel(option) {
  return option?.nome || option?.name || option?.email || option?.label || "";
}

function sameOption(option, value) {
  return Number(option?.id) === Number(value?.id);
}

function toShare(targetType, targetId, permission) {
  return { targetType, targetId: Number(targetId), permission };
}

function unwrap(res) {
  if (res && typeof res === "object" && "data" in res) return res.data;
  return res;
}

function buildFormFromCalendar(calendar, userOptions, positionOptions, companyOptions) {
  const shares = Array.isArray(calendar?.shares) ? calendar.shares : [];
  const users = [];
  const positions = [];
  const companies = [];
  const shareTargets = [];
  let permission = "manage";

  for (const share of shares) {
    permission = share.permission || permission;
    if (share.targetType === "user") {
      shareTargets.push("users");
      const found = userOptions.find((u) => Number(u.id) === Number(share.targetId));
      if (found) users.push(found);
      else users.push({ id: share.targetId, nome: `Usuário #${share.targetId}` });
    } else if (share.targetType === "position") {
      shareTargets.push("positions");
      const found = positionOptions.find((p) => Number(p.id) === Number(share.targetId));
      if (found) positions.push(found);
      else positions.push({ id: share.targetId, nome: `Cargo #${share.targetId}` });
    } else if (share.targetType === "project") {
      shareTargets.push("companies");
      const found = companyOptions.find((c) => Number(c.id) === Number(share.targetId));
      if (found) companies.push(found);
      else companies.push({ id: share.targetId, nome: `Projeto #${share.targetId}` });
    }
  }

  return {
    name: calendar?.name || "",
    type: calendar?.type === "resource" ? "resource" : "shared",
    color: calendar?.color || "#7b1fa2",
    permission,
    shareTargets: shareTargets.length ? [...new Set(shareTargets)] : ["users"],
    users,
    positions,
    companies,
    googleCalendarId: calendar?.googleCalendarId || "",
  };
}

export default function AgendaCalendarFilter({
  calendars = [],
  selectedCalendarIds,
  onToggleCalendar,
  onCreateCalendar,
  onUpdateCalendar,
  onDeleteCalendar,
  creating = false,
  updating = false,
  deleting = false,
  compact = false,
}) {
  const theme = useTheme();
  const { userData } = useContext(AuthContext);
  const currentUserId = Number(userData?.id);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCalendar, setEditingCalendar] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuCalendar, setMenuCalendar] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionError, setActionError] = useState("");
  const { data: usersData } = useUsers(1, 200, "", "todos");
  const { data: positionsData } = usePositions(1, 200, "");
  const { data: companiesData } = useCompanies(1, 200, "");

  const userOptions = useMemo(() => usersData?.users || [], [usersData]);
  const positionOptions = useMemo(() => positionsData?.positions || [], [positionsData]);
  const companyOptions = useMemo(() => companiesData?.companies || [], [companiesData]);

  const isEdit = Boolean(editingCalendar);
  const isPersonalEdit = isEdit && editingCalendar?.type === "personal";
  const isResource = form.type === "resource";
  const selectedType = calendarTypeOptions.find((option) => option.value === form.type) || calendarTypeOptions[0];
  const SelectedTypeIcon = isPersonalEdit ? CalendarMonthOutlinedIcon : selectedType.Icon;
  const shareWithUsers = form.shareTargets.includes("users");
  const shareWithPositions = form.shareTargets.includes("positions");
  const shareWithCompanies = form.shareTargets.includes("companies");
  const selectedShareCount =
    (shareWithUsers ? form.users.length : 0) +
    (shareWithPositions ? form.positions.length : 0) +
    (shareWithCompanies ? form.companies.length : 0);
  // Criação exige ao menos um acesso; agenda pessoal pode ficar só compartilhada parcialmente (0 ok).
  const canSubmit = Boolean(form.name.trim() && (isPersonalEdit || selectedShareCount > 0));
  const saving = creating || updating;
  const createHint = (() => {
    if (!form.name.trim()) return "Dê um nome para continuar.";
    if (!isPersonalEdit && !selectedShareCount) {
      return "Escolha pelo menos uma pessoa, cargo ou empresa/projeto.";
    }
    if (isPersonalEdit && !selectedShareCount) return "Sem compartilhamento — só você verá esta agenda.";
    return `${selectedShareCount} ${selectedShareCount === 1 ? "acesso selecionado" : "acessos selecionados"}`;
  })();

  const { data: googleStatus } = useQuery(
    ["google-calendar-status"],
    async () => unwrap(await getGoogleCalendarStatus()) || {},
    { enabled: dialogOpen, staleTime: 30_000 }
  );
  const googleConnected = Boolean(googleStatus?.connected);
  const googleNeedsReauth = Boolean(googleStatus?.needsReauth);

  const {
    data: googleCalendarsData,
    isLoading: loadingGoogleCalendars,
    error: googleCalendarsError,
  } = useQuery(
    ["google-calendar-list"],
    async () => unwrap(await fetchGoogleCalendarList()) || {},
    {
      enabled: dialogOpen && googleConnected && !googleNeedsReauth,
      staleTime: 60_000,
      retry: false,
    }
  );
  const googleCalendarOptions = googleCalendarsData?.calendars || [];

  useEffect(() => {
    if (!dialogOpen || !editingCalendar) return;
    setForm(buildFormFromCalendar(editingCalendar, userOptions, positionOptions, companyOptions));
  }, [dialogOpen, editingCalendar, userOptions, positionOptions, companyOptions]);

  const fieldSx = {
    "& .MuiOutlinedInput-root": { borderRadius: 2 },
    "& .MuiFormHelperText-root": { mx: 0 },
  };

  const optionCardSx = (selected, selectedColor = form.color) => ({
    p: 1.5,
    borderRadius: 2.5,
    cursor: "pointer",
    border: "1px solid",
    borderColor: selected ? alpha(selectedColor, 0.55) : "divider",
    bgcolor: selected ? alpha(selectedColor, theme.palette.mode === "dark" ? 0.22 : 0.08) : "background.paper",
    boxShadow: selected ? `0 12px 30px ${alpha(selectedColor, 0.16)}` : "0 8px 24px rgba(15, 23, 42, 0.05)",
    transition: "border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease",
    "&:hover": {
      borderColor: alpha(selectedColor, 0.55),
      boxShadow: `0 14px 34px ${alpha(selectedColor, 0.14)}`,
    },
    "&:focus-visible": {
      outline: `3px solid ${alpha(selectedColor, 0.24)}`,
      outlineOffset: 2,
    },
  });

  const permissionOptions = [
    {
      value: "view",
      title: "Só visualizar",
      description: isResource ? "Veem disponibilidade, mas não reservam." : "Veem a agenda, mas não criam compromissos.",
      Icon: VisibilityOutlinedIcon,
    },
    {
      value: "create",
      title: isResource ? "Reservar horários" : "Criar compromissos",
      description: isResource ? "Podem reservar horários livres." : "Podem adicionar novos compromissos.",
      Icon: EditCalendarOutlinedIcon,
    },
    {
      value: "manage",
      title: "Gerenciar tudo",
      description: isResource
        ? "Podem reservar, editar e cancelar reservas."
        : "Podem criar, editar e cancelar compromissos.",
      Icon: AdminPanelSettingsOutlinedIcon,
    },
  ];

  const resetAndClose = () => {
    setDialogOpen(false);
    setEditingCalendar(null);
    setForm(emptyForm);
    setActionError("");
  };

  const openCreate = () => {
    setEditingCalendar(null);
    setForm(emptyForm);
    setActionError("");
    setDialogOpen(true);
  };

  const openEdit = (calendar) => {
    setMenuAnchor(null);
    setMenuCalendar(null);
    setEditingCalendar(calendar);
    const next = buildFormFromCalendar(calendar, userOptions, positionOptions, companyOptions);
    if (calendar?.type === "personal" && !(calendar.shares || []).length) {
      next.permission = "view";
      next.shareTargets = ["users"];
    }
    setForm(next);
    setActionError("");
    setDialogOpen(true);
  };

  const toggleShareTarget = (target) => {
    setForm((current) => {
      const isSelected = current.shareTargets.includes(target);
      const shareTargets = isSelected
        ? current.shareTargets.filter((item) => item !== target)
        : [...current.shareTargets, target];

      return {
        ...current,
        shareTargets,
        users: target === "users" && isSelected ? [] : current.users,
        positions: target === "positions" && isSelected ? [] : current.positions,
        companies: target === "companies" && isSelected ? [] : current.companies,
      };
    });
  };

  const buildPayload = () => {
    const shares = [
      ...(shareWithUsers ? form.users.map((user) => toShare("user", user.id, form.permission)) : []),
      ...(shareWithPositions ? form.positions.map((position) => toShare("position", position.id, form.permission)) : []),
      ...(shareWithCompanies
        ? form.companies.map((company) => toShare("project", company.id, form.permission))
        : []),
    ];
    if (isPersonalEdit) {
      return {
        name: form.name.trim(),
        color: form.color,
        shares,
      };
    }
    return {
      name: form.name.trim(),
      type: form.type,
      color: form.color,
      shares,
      googleCalendarId: form.googleCalendarId || null,
    };
  };

  const handleSubmit = async () => {
    setActionError("");
    try {
      const payload = buildPayload();
      if (isEdit) {
        await onUpdateCalendar?.({ calendarId: editingCalendar.id, payload });
      } else {
        await onCreateCalendar?.(payload);
      }
      resetAndClose();
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Não foi possível salvar a agenda."));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await onDeleteCalendar?.(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Não foi possível excluir a agenda."));
      setDeleteTarget(null);
    }
  };

  const handleConnectGoogle = async () => {
    try {
      const r = await getGoogleCalendarOauthUrl();
      const url = r?.url || unwrap(r)?.url;
      if (url) window.location.assign(url);
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Não foi possível abrir a conexão com o Google."));
    }
  };

  if (!calendars.length) return null;

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: compact ? 0.75 : 1.25 }}
      >
        {!compact ? (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mr: 0.5 }}>
            <CalendarMonthOutlinedIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              Agendas
            </Typography>
          </Stack>
        ) : null}
        {calendars.map((calendar) => {
          const selected = selectedCalendarIds?.has(Number(calendar.id));
          const color = calendar.color || theme.palette.primary.main;
          const isOwner = Number(calendar.ownerUserId) === currentUserId;
          const canConfigure = isOwner;
          const linked = Boolean(calendar.googleCalendarId && calendar.type !== "personal");
          return (
            <Stack key={calendar.id} direction="row" alignItems="center" spacing={0} sx={{ mr: 0.25 }}>
              <Tooltip
                title={
                  linked
                    ? `${calendar.name} · vinculada ao Google`
                    : calendar.canManage
                      ? "Você pode gerenciar"
                      : "Somente leitura"
                }
              >
                <Chip
                  clickable
                  size="small"
                  icon={linked ? <LinkIcon sx={{ fontSize: "14px !important" }} /> : undefined}
                  label={
                    calendar.ownerDeleted && Number(calendar.ownerUserId) !== currentUserId
                      ? `${calendar.name} (usuário excluído)`
                      : calendar.name
                  }
                  onClick={() => onToggleCalendar?.(calendar.id)}
                  sx={{
                    height: compact ? 22 : 26,
                    fontWeight: selected ? 700 : 500,
                    opacity: selected ? 1 : 0.55,
                    bgcolor: selected ? alpha(color, theme.palette.mode === "dark" ? 0.28 : 0.12) : "transparent",
                    color: selected ? color : "text.secondary",
                    border: `1px solid ${selected ? alpha(color, 0.45) : theme.palette.divider}`,
                    "& .MuiChip-label": { maxWidth: compact ? 120 : 180 },
                    borderTopRightRadius: canConfigure ? 0 : undefined,
                    borderBottomRightRadius: canConfigure ? 0 : undefined,
                  }}
                />
              </Tooltip>
              {canConfigure ? (
                <IconButton
                  size="small"
                  aria-label={`Configurar ${calendar.name}`}
                  onClick={(e) => {
                    setMenuAnchor(e.currentTarget);
                    setMenuCalendar(calendar);
                  }}
                  sx={{
                    width: compact ? 22 : 26,
                    height: compact ? 22 : 26,
                    borderRadius: "0 13px 13px 0",
                    border: `1px solid ${selected ? alpha(color, 0.45) : theme.palette.divider}`,
                    borderLeft: 0,
                    color: selected ? color : "text.secondary",
                    bgcolor: selected ? alpha(color, theme.palette.mode === "dark" ? 0.28 : 0.12) : "transparent",
                  }}
                >
                  <MoreVertIcon sx={{ fontSize: 14 }} />
                </IconButton>
              ) : null}
            </Stack>
          );
        })}
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ textTransform: "none", fontWeight: 700, minWidth: 0, px: 1 }}
        >
          {compact ? "Nova" : "Nova agenda"}
        </Button>
      </Stack>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
          setMenuCalendar(null);
        }}
      >
        <MenuItem
          onClick={() => menuCalendar && openEdit(menuCalendar)}
          dense
        >
          <EditOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          {menuCalendar?.type === "personal" ? "Compartilhar agenda" : "Editar parametrizações"}
        </MenuItem>
        {menuCalendar?.type !== "personal" ? (
          <MenuItem
            onClick={() => {
              setDeleteTarget(menuCalendar);
              setMenuAnchor(null);
              setMenuCalendar(null);
            }}
            dense
            sx={{ color: "error.main" }}
          >
            <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} />
            Excluir agenda
          </MenuItem>
        ) : null}
      </Menu>

      <Dialog
        open={dialogOpen}
        onClose={saving ? undefined : resetAndClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              px: 3,
              pt: 3,
              pb: 2.5,
              borderBottom: 1,
              borderColor: "divider",
              background: `linear-gradient(135deg, ${alpha(form.color, 0.16)} 0%, ${alpha(
                theme.palette.background.paper,
                0.96
              )} 64%)`,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2.5,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha(form.color, 0.14),
                  color: form.color,
                  border: `1px solid ${alpha(form.color, 0.22)}`,
                }}
              >
                <SelectedTypeIcon fontSize="small" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" fontWeight={800} lineHeight={1.2}>
                  {isPersonalEdit
                    ? "Compartilhar agenda pessoal"
                    : isEdit
                      ? "Editar agenda compartilhada"
                      : "Criar agenda compartilhada"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  {isPersonalEdit
                    ? "Escolha quem poderá ver sua agenda pessoal. Quem tiver acesso vê seus compromissos ao agendar juntos."
                    : isEdit
                      ? "Atualize nome, acessos e vínculo com o Google Calendar."
                      : "Defina quem verá a agenda e o que poderá fazer. A agenda pessoal continua privada até você compartilhá-la."}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2.5, maxHeight: "72vh", overflowY: "auto" }}>
          <Stack spacing={2.5}>
            {actionError ? <Alert severity="error">{actionError}</Alert> : null}

            <Box>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
                Identifique a agenda
              </Typography>
              <TextField
                label={isResource ? "Nome da sala ou recurso" : "Nome da agenda"}
                placeholder={isResource ? "Ex.: Sala de reunião, Carro 01" : "Ex.: Vendas, Suporte, Dr. João"}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                fullWidth
                size="small"
                required
                sx={fieldSx}
              />

              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  Cor no calendário
                </Typography>
                {colorOptions.map((color) => (
                  <Box
                    key={color}
                    component="button"
                    type="button"
                    aria-label={`Usar cor ${color}`}
                    aria-pressed={form.color === color}
                    onClick={() => setForm((f) => ({ ...f, color }))}
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: "50%",
                      border: `2px solid ${form.color === color ? theme.palette.text.primary : "transparent"}`,
                      bgcolor: color,
                      cursor: "pointer",
                      p: 0,
                      appearance: "none",
                      boxShadow: `0 0 0 3px ${alpha(color, 0.18)}`,
                    }}
                  />
                ))}
              </Stack>
            </Box>

            {!isPersonalEdit ? (
            <Box>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
                Que tipo de agenda?
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                {calendarTypeOptions.map((option) => {
                  const Icon = option.Icon;
                  const selected = form.type === option.value;
                  return (
                    <Box
                      key={option.value}
                      role="button"
                      tabIndex={0}
                      aria-pressed={selected}
                      onClick={() => setForm((f) => ({ ...f, type: option.value }))}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setForm((f) => ({ ...f, type: option.value }));
                        }
                      }}
                      sx={{ ...optionCardSx(selected), flex: 1 }}
                    >
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Icon fontSize="small" sx={{ color: selected ? form.color : "text.secondary", mt: 0.25 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={800}>
                            {option.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                        {selected ? <CheckCircleOutlineIcon fontSize="small" sx={{ color: form.color }} /> : null}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
            ) : null}

            <Box>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.25 }}>
                {isResource ? "Como esse recurso poderá ser usado?" : "O que as pessoas poderão fazer?"}
              </Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {permissionOptions.map((option) => {
                  const Icon = option.Icon;
                  const selected = form.permission === option.value;
                  return (
                    <Box
                      key={option.value}
                      role="button"
                      tabIndex={0}
                      aria-pressed={selected}
                      onClick={() => setForm((f) => ({ ...f, permission: option.value }))}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setForm((f) => ({ ...f, permission: option.value }));
                        }
                      }}
                      sx={optionCardSx(selected)}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Icon fontSize="small" sx={{ color: selected ? form.color : "text.secondary" }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={800}>
                            {option.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                        {selected ? <CheckCircleOutlineIcon fontSize="small" sx={{ color: form.color }} /> : null}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.25 }}>
                {isResource ? "Quem pode reservar?" : "Quem terá acesso?"}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ mt: 1 }}>
                {shareTargetOptions.map((option) => {
                  const Icon = option.Icon;
                  const selected = form.shareTargets.includes(option.value);
                  return (
                    <Box
                      key={option.value}
                      role="button"
                      tabIndex={0}
                      aria-pressed={selected}
                      onClick={() => toggleShareTarget(option.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          toggleShareTarget(option.value);
                        }
                      }}
                      sx={{ ...optionCardSx(selected), flex: 1 }}
                    >
                      <Stack spacing={0.75}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <Icon fontSize="small" sx={{ color: selected ? form.color : "text.secondary" }} />
                          <Typography variant="body2" fontWeight={800}>
                            {option.title}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>

              <Stack spacing={1.5} sx={{ mt: form.shareTargets.length ? 1.5 : 0 }}>
                {shareWithUsers ? (
                  <Autocomplete
                    multiple
                    size="small"
                    options={userOptions}
                    value={form.users}
                    onChange={(_, value) => setForm((f) => ({ ...f, users: value }))}
                    getOptionLabel={(option) => optionLabel(option)}
                    isOptionEqualToValue={sameOption}
                    renderInput={(params) => (
                      <TextField {...params} label="Pessoas com acesso" placeholder="Buscar pessoas" sx={fieldSx} />
                    )}
                  />
                ) : null}
                {shareWithPositions ? (
                  <Autocomplete
                    multiple
                    size="small"
                    options={positionOptions}
                    value={form.positions}
                    onChange={(_, value) => setForm((f) => ({ ...f, positions: value }))}
                    getOptionLabel={(option) => optionLabel(option)}
                    isOptionEqualToValue={sameOption}
                    renderInput={(params) => (
                      <TextField {...params} label="Cargos com acesso" placeholder="Buscar cargos" sx={fieldSx} />
                    )}
                  />
                ) : null}
                {shareWithCompanies ? (
                  <Autocomplete
                    multiple
                    size="small"
                    options={companyOptions}
                    value={form.companies}
                    onChange={(_, value) => setForm((f) => ({ ...f, companies: value }))}
                    getOptionLabel={(option) => optionLabel(option)}
                    isOptionEqualToValue={sameOption}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Empresas ou projetos com acesso"
                        placeholder="Buscar empresas ou projetos"
                        sx={fieldSx}
                      />
                    )}
                  />
                ) : null}
              </Stack>
            </Box>

            {!isPersonalEdit ? (
            <Box>
              <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>
                Google Calendar (opcional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                Vincule a uma agenda Google existente. Quem tem acesso no Xpro vê os eventos sincronizados.
              </Typography>
              {!googleConnected ? (
                <Alert
                  severity="info"
                  action={
                    <Button color="inherit" size="small" onClick={handleConnectGoogle}>
                      Conectar
                    </Button>
                  }
                >
                  Conecte o Google no seu perfil para escolher um calendário.
                </Alert>
              ) : googleNeedsReauth || googleCalendarsError ? (
                <Alert
                  severity="warning"
                  action={
                    <Button color="inherit" size="small" onClick={handleConnectGoogle}>
                      Reconectar
                    </Button>
                  }
                >
                  Reconecte o Google Agenda para listar calendários (novas permissões).
                </Alert>
              ) : (
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel id="google-cal-link-label">Calendário Google</InputLabel>
                  <Select
                    labelId="google-cal-link-label"
                    label="Calendário Google"
                    value={form.googleCalendarId || ""}
                    disabled={loadingGoogleCalendars}
                    onChange={(e) => setForm((f) => ({ ...f, googleCalendarId: e.target.value }))}
                  >
                    <MenuItem value="">
                      <em>Sem vínculo</em>
                    </MenuItem>
                    {googleCalendarOptions.map((cal) => (
                      <MenuItem key={cal.id} value={cal.id}>
                        {cal.summary}
                        {cal.primary ? " (principal)" : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: 1,
            borderColor: "divider",
            bgcolor: alpha(theme.palette.background.default, theme.palette.mode === "dark" ? 0.2 : 0.65),
          }}
        >
          <Typography variant="caption" color={canSubmit ? "text.secondary" : "error.main"} sx={{ flex: 1 }}>
            {createHint}
          </Typography>
          <Button onClick={resetAndClose} disabled={saving} sx={{ textTransform: "none", fontWeight: 700 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving || !canSubmit}
            sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2, px: 2.25 }}
          >
            {isEdit ? "Salvar" : "Criar agenda"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Excluir agenda"
        content={
          deleteTarget
            ? `Excluir a agenda "${deleteTarget.name}"? Os compromissos dela deixam de aparecer para a equipe.`
            : ""
        }
        confirmText="Excluir"
        cancelText="Cancelar"
        loading={deleting}
        onClose={() => (deleting ? null : setDeleteTarget(null))}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}
